import type { CareStateEvent, CareStateRow, CareStateUpdate } from '../model/types.js';

/**
 * Apply server events and/or client updates to a CareState database.
 *
 * This function is:
 * - Pure: it never mutates `oldDatabase` (it clones tables + rows before applying operations).
 * - Deterministic: given the same inputs, produces the same output.
 *
 * Semantics intentionally mirror the `multiplayer-demo` prototype:
 * - Server events (`replace`) are unconditional and represent changes that already happened.
 * - Client updates (`create`/`update`/`delete`) may be conditional via `conditionalOldValue`.
 */
export function applyCareStateOperations<DbT extends { [K in keyof DbT]: CareStateRow[] }>(
  oldDatabase: DbT,
  events: ReadonlyArray<CareStateEvent<DbT>>,
  updates: ReadonlyArray<CareStateUpdate<DbT>>
): DbT {
  const newDatabase = { ...oldDatabase } as DbT;
  for (const key of Object.keys(oldDatabase) as Array<Extract<keyof DbT, string>>) {
    const table = oldDatabase[key];
    if (table === undefined) {
      continue;
    }
    newDatabase[key] = [...table] as DbT[typeof key];
  }

  const operations: Array<CareStateEvent<DbT> | CareStateUpdate<DbT>> = [...events, ...updates];
  for (const operation of operations) {
    let table = newDatabase[operation.table];
    if (table === undefined) {
      // Backend may introduce new tables before the frontend schema catches up.
      // Initialise lazily so downstream operations don't crash.
      table = [] as unknown as DbT[Extract<keyof DbT, string>];
      newDatabase[operation.table] = table;
      console.error(`Table ${operation.table} not found in new database, initialising lazily`);
    }

    switch (operation.type) {
      // Update operations, which can be conditional for transactionality/concurrency.
      case 'create': {
        if (table.some((row) => row.id === operation.row.id)) {
          break;
        }
        table.push(operation.row);
        break;
      }
      case 'update': {
        const updateRowIndex = table.findIndex((row) => row.id === operation.row.id);
        if (updateRowIndex === -1) {
          // If conditional, fail (no-op). If unconditional, upsert by insert.
          if (operation.conditionalOldValue !== null) {
            break;
          }
          table.push(operation.row);
          break;
        }

        if (
          operation.conditionalOldValue === null ||
          table[updateRowIndex] === operation.conditionalOldValue
        ) {
          table[updateRowIndex] = operation.row;
        }
        break;
      }
      case 'delete': {
        const deleteRowIndex = table.findIndex((row) => row.id === operation.id);
        if (deleteRowIndex === -1) {
          break;
        }
        if (
          operation.conditionalOldValue === null ||
          table[deleteRowIndex] === operation.conditionalOldValue
        ) {
          table.splice(deleteRowIndex, 1);
        }
        break;
      }
      // Events: unconditional — they represent a thing that already happened.
      case 'replace': {
        const replaceRowIndex = table.findIndex((row) => row.id === operation.id);
        if (replaceRowIndex === -1) {
          if (operation.newValue === null) {
            break;
          }
          table.push(operation.newValue);
          break;
        }

        if (operation.newValue === null) {
          table.splice(replaceRowIndex, 1);
          break;
        }

        table[replaceRowIndex] = operation.newValue;
        break;
      }
      default: {
        const _exhaustive: never = operation;
        void _exhaustive;
        throw new Error('Unknown CareState operation');
      }
    }
  }

  return newDatabase;
}
